import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useSimplifiedUserData } from '@/providers/SimplifiedUserDataProvider';
import { useDialogDataContext } from "@/providers/DialogDataProvider";
import { dialogStore, DialogField, DialogOption, DialogVariable } from '@/lib/stores/dialogStore';
import { useToast } from '@/hooks/use-toast';
import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
import { Label } from '@/components/ui/label';
import { TextAreaElement } from '@/components/wizards/wizard-elements/TextAreaElement';
import { Input } from '@/components/ui/InputDefault';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/DefaultSelectDropdown';
import { Button } from '@/components/ui/button';
import { Save, Loader2, Plus, Trash2 } from 'lucide-react';
import { apiService } from '@/lib/api-functions/api-service';
import { Checkbox } from '@/components/ui/checkbox';
import { useDialogSectionOptional } from '@/contexts/DialogSectionContext';
import { getCachedTypes, TypeElement } from '@/lib/stores/typesStore';
import { getCachedObjects } from '@/lib/stores/objectsStore';
import { useAutoSave } from '@/hooks/useAutoSave';
import { useActivityTracker } from '@/hooks/useActivityTracker';
import { getTypeItemById } from '@/lib/utils/typeStoreResolver';
import {
  useDisplayLanguageOptional,
  resolveFieldEditableText,
  resolveOptionEditableText,
  saveFieldTranslation,
} from '@/contexts/DisplayLanguageContext';

interface DialogSectionSidebarProps {
  currentSectionIndex?: number;
  onSectionUpdate?: (sectionIndex: number, updates: any) => void;
}

export default function StructureSidebarDialogSection({
  currentSectionIndex = 0,
  onSectionUpdate
}: DialogSectionSidebarProps) {
  const { t } = usePlatformTranslation();
  const params = useParams();
  const location = useLocation();
  const { toast } = useToast();
  const { selectedGame } = useSimplifiedUserData();
  const { dialogs } = useDialogDataContext();

  // Display language context for translation-aware editing
  const displayLanguageCtx = useDisplayLanguageOptional();
  const displayLanguage = displayLanguageCtx?.displayLanguage ?? null;
  const effectiveGameId = selectedGame?.id;

  // State
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editedSection, setEditedSection] = useState<any>(null);
  const [lastSavedData, setLastSavedData] = useState<string | null>(null);

  // Track translation edits separately so we never corrupt the source text
  // Key = field.id for field text, "fieldId:optionId" for option text
  const [translationEdits, setTranslationEdits] = useState<Record<string, string>>({});

  // Track user activity for smart auto-save (don't save while typing)
  const { activityTimestamp, recordActivity } = useActivityTracker();
  
  // Store save handler ref for auto-save
  const saveHandlerRef = useRef<(() => Promise<void>) | undefined>();

  // Get current dialog
  const currentDialog = useMemo(() => {
    const path = location.pathname;
    if (!path.startsWith('/dialogs/') || !params.dialogId) return null;
    return dialogs.find(dialog => dialog.id === params.dialogId);
  }, [location.pathname, params.dialogId, dialogs]);

  // Get current section from context (real-time data from wizard)
  const dialogSectionContext = useDialogSectionOptional();
  const currentSection = dialogSectionContext?.currentSection || null;

  // Get all section identifiers for dropdown options
  const allSectionIdentifiers = useMemo(() => {
    if (!currentDialog?.data?.sections) return [];
    return currentDialog.data.sections.map(s => ({
      identifier: s.section_identifier,
      name: s.name || s.section_identifier
    }));
  }, [currentDialog]);

  // Get all dialogs for jump options
  const allDialogs = useMemo(() => {
    return dialogs.map(d => ({
      id: d.id,
      name: d.name,
      dialog_key: d.dialog_key
    }));
  }, [dialogs]);

  // Get speaker configuration from dialog metadata (real-time from context)
  // This ensures speaker settings update immediately when changed in the wizard
  const speakerMode = dialogSectionContext?.dialogMetadata?.speakerMode || currentDialog?.data?.speakerMode || 'none';
  const speakerTypeId = dialogSectionContext?.dialogMetadata?.speakerTypeId || currentDialog?.data?.speakerTypeId || null;
  const speakerTemplateIds = useMemo(
    () => dialogSectionContext?.dialogMetadata?.speakerTemplateIds || currentDialog?.data?.speakerTemplateIds || [],
    [dialogSectionContext?.dialogMetadata?.speakerTemplateIds, currentDialog?.data?.speakerTemplateIds]
  );

  // Get dialog variables from metadata for setter fields
  const dialogVariables = useMemo(
    () => dialogSectionContext?.dialogMetadata?.dialogVariables || currentDialog?.data?.variables || [],
    [dialogSectionContext?.dialogMetadata?.dialogVariables, currentDialog?.data?.variables]
  );

  // Get type elements for speaker dropdown (if speakerMode is 'type')
  const typeElements = useMemo(() => {
    if (!selectedGame?.id || speakerMode !== 'type' || !speakerTypeId) return [];
    const types = getCachedTypes(selectedGame.id);
    const selectedType = types.find(t => t.id === speakerTypeId);
    return selectedType?.data || [];
  }, [speakerMode, speakerTypeId, selectedGame?.id]);

  // Get objects for speaker dropdown (if speakerMode is 'object')
  const availableObjects = useMemo(() => {
    if (!selectedGame?.id || speakerMode !== 'object' || !speakerTemplateIds.length) return [];
    const objects = getCachedObjects(selectedGame.id);
    return objects.filter(obj =>
      speakerTemplateIds.includes(obj.blueprint_id) &&
      obj.type === 'glossary'
    );
  }, [speakerMode, speakerTemplateIds, selectedGame?.id]);

  // Reset translation edits when language changes
  useEffect(() => {
    setTranslationEdits({});
  }, [displayLanguage]);

  // Load section data when current section changes
  useEffect(() => {
    if (!currentSection) {
      setEditedSection(null);
      setIsEditing(false);
      setLastSavedData(null);
      setTranslationEdits({});
      return;
    }

    // Only sync from context if not actively editing
    // This prevents overwriting user changes while they're typing
    if (!isEditing) {
      const currentSectionStr = JSON.stringify(currentSection);
      
      // Don't overwrite with the same data we just saved
      // This prevents race conditions where stale context data overwrites our save
      if (lastSavedData && currentSectionStr === lastSavedData) {
        // Data matches what we just saved, no need to sync
        return;
      }

      // Deep clone to avoid reference issues
      setEditedSection(JSON.parse(currentSectionStr));
      
      // Clear lastSavedData once we've synced new data from context
      if (lastSavedData && currentSectionStr !== lastSavedData) {
        setLastSavedData(null);
      }
    }
  }, [currentSection, isEditing, lastSavedData]);

  // Listen for dialog section updates from other parts of the app (flow/edit mode)
  // Note: We don't reset editedSection here anymore because:
  // 1. The effect at lines 111-120 already handles syncing from currentSection
  // 2. Resetting here with a stale currentSection value causes the save bug
  // 3. After a successful save, we want to keep the saved data, not reset from context
  useEffect(() => {
    const handleDialogUpdate = (event: CustomEvent) => {
      const { dialogId, sectionIndex } = event.detail;
      if (dialogId === currentDialog?.id && sectionIndex === currentSectionIndex) {
        // Just reset the editing state - don't reset editedSection
        // The context sync effect will handle updating editedSection from the latest currentSection
        setIsEditing(false);
      }
    };

    window.addEventListener('dialog-section-updated', handleDialogUpdate as EventListener);
    return () => {
      window.removeEventListener('dialog-section-updated', handleDialogUpdate as EventListener);
    };
  }, [currentDialog?.id, currentSectionIndex]);

  // ── Translation-aware helpers ──────────────────────────────────────

  /** True when editing a non-source language */
  const isTranslationMode = displayLanguage !== null;

  /** Get the value to show in a field's text editor */
  const getFieldTextValue = (field: DialogField): string => {
    if (isTranslationMode && field.string_id && field.translatable) {
      if (translationEdits[field.id] !== undefined) return translationEdits[field.id];
      return resolveFieldEditableText(field, displayLanguage, effectiveGameId);
    }
    return field.text || '';
  };

  /** Get the value to show in an option's text editor */
  const getOptionTextValue = (field: DialogField, option: DialogOption): string => {
    const key = `${field.id}:${option.id}`;
    if (isTranslationMode && option.string_id) {
      if (translationEdits[key] !== undefined) return translationEdits[key];
      return resolveOptionEditableText(option, displayLanguage, effectiveGameId);
    }
    return option.option_text || '';
  };

  /** Handle field text change – translation-aware */
  const handleFieldTextChange = (field: DialogField, value: string) => {
    if (isTranslationMode && field.string_id && field.translatable) {
      // Translation mode: track edit separately, don't touch source text
      setTranslationEdits(prev => ({ ...prev, [field.id]: value }));
      if (!isEditing) setIsEditing(true);
      setLastSavedData(null);
      recordActivity();
    } else {
      // Source mode: update field.text as before
      updateField(field.id, { text: value });
    }
  };

  /** Handle option text change – translation-aware */
  const handleOptionTextChange = (field: DialogField, option: DialogOption, optionIndex: number, value: string) => {
    const key = `${field.id}:${option.id}`;
    if (isTranslationMode && option.string_id) {
      // Translation mode: track edit separately
      setTranslationEdits(prev => ({ ...prev, [key]: value }));
      if (!isEditing) setIsEditing(true);
      setLastSavedData(null);
      recordActivity();
    } else {
      // Source mode: update option_text as before
      updateOption(field.id, optionIndex, { option_text: value });
    }
  };

  // Handle field update
  const updateField = (fieldId: string, updates: Partial<DialogField>) => {
    if (!editedSection) return;
    if (!isEditing) setIsEditing(true);

    const newFields = editedSection.fields.map((field: DialogField) =>
      field.id === fieldId ? { ...field, ...updates } : field
    );

    const updatedSection = { ...editedSection, fields: newFields };
    setEditedSection(updatedSection);

    // Clear saved data tracking since we're making new changes
    setLastSavedData(null);

    // Track activity for smart auto-save
    recordActivity();

    // Real-time update - notify context immediately
    if (onSectionUpdate) {
      onSectionUpdate(currentSectionIndex, updatedSection);
    }
  };

  // Handle option update
  const updateOption = (fieldId: string, optionIndex: number, updates: Partial<DialogOption>) => {
    if (!editedSection) return;
    if (!isEditing) setIsEditing(true);

    const newFields = editedSection.fields.map((field: DialogField) => {
      if (field.id === fieldId && field.options) {
        const newOptions = [...field.options];
        newOptions[optionIndex] = { ...newOptions[optionIndex], ...updates };
        return { ...field, options: newOptions };
      }
      return field;
    });

    const updatedSection = { ...editedSection, fields: newFields };
    setEditedSection(updatedSection);

    // Clear saved data tracking since we're making new changes
    setLastSavedData(null);

    // Track activity for smart auto-save
    recordActivity();

    // Real-time update - notify context immediately
    if (onSectionUpdate) {
      onSectionUpdate(currentSectionIndex, updatedSection);
    }
  };

  // Add new option
  const addOption = (fieldId: string) => {
    if (!editedSection) return;
    if (!isEditing) setIsEditing(true);

    const newFields = editedSection.fields.map((field: DialogField) => {
      if (field.id === fieldId) {
        const existingOptions = field.options || [];
        const newOption: DialogOption = {
          id: `option_${Date.now()}_${Math.random()}`,
          field_id: fieldId,
          option_text: '',
          translatable: true,
          next_section: null,
          next_dialog: null,
          next_dialog_section: null,
          order_index: existingOptions.length
        };
        return { ...field, options: [...existingOptions, newOption] };
      }
      return field;
    });

    const updatedSection = { ...editedSection, fields: newFields };
    setEditedSection(updatedSection);

    // Clear saved data tracking since we're making new changes
    setLastSavedData(null);

    // Real-time update - notify context immediately
    if (onSectionUpdate) {
      onSectionUpdate(currentSectionIndex, updatedSection);
    }
  };

  // Add new condition option
  const addConditionOption = (fieldId: string) => {
    if (!editedSection) return;
    if (!isEditing) setIsEditing(true);

    const newFields = editedSection.fields.map((field: DialogField) => {
      if (field.id === fieldId) {
        const existingConditionOptions = field.conditionOptions || [];
        const newConditionOption = {
          id: `condition_option_${Date.now()}_${Math.random()}`,
          condition_value: '',
          operator: '==' as const,
          next_section: null,
          next_dialog: null,
          next_dialog_section: null,
          order_index: existingConditionOptions.length
        };
        return { ...field, conditionOptions: [...existingConditionOptions, newConditionOption] };
      }
      return field;
    });

    const updatedSection = { ...editedSection, fields: newFields };
    setEditedSection(updatedSection);
    setLastSavedData(null);

    if (onSectionUpdate) {
      onSectionUpdate(currentSectionIndex, updatedSection);
    }
  };

  // Remove condition option
  const removeConditionOption = (fieldId: string, optionIndex: number) => {
    if (!editedSection) return;
    if (!isEditing) setIsEditing(true);

    const newFields = editedSection.fields.map((field: DialogField) => {
      if (field.id === fieldId && field.conditionOptions) {
        const newConditionOptions = field.conditionOptions.filter((_, idx) => idx !== optionIndex);
        return { ...field, conditionOptions: newConditionOptions };
      }
      return field;
    });

    const updatedSection = { ...editedSection, fields: newFields };
    setEditedSection(updatedSection);
    setLastSavedData(null);

    if (onSectionUpdate) {
      onSectionUpdate(currentSectionIndex, updatedSection);
    }
  };

  // Update condition option
  const updateConditionOption = (fieldId: string, optionIndex: number, updates: any) => {
    if (!editedSection) return;
    if (!isEditing) setIsEditing(true);

    const newFields = editedSection.fields.map((field: DialogField) => {
      if (field.id === fieldId && field.conditionOptions) {
        const newConditionOptions = [...field.conditionOptions];
        newConditionOptions[optionIndex] = { ...newConditionOptions[optionIndex], ...updates };
        return { ...field, conditionOptions: newConditionOptions };
      }
      return field;
    });

    const updatedSection = { ...editedSection, fields: newFields };
    setEditedSection(updatedSection);
    setLastSavedData(null);
    recordActivity();

    if (onSectionUpdate) {
      onSectionUpdate(currentSectionIndex, updatedSection);
    }
  };

  // Remove option
  const removeOption = (fieldId: string, optionIndex: number) => {
    if (!editedSection) return;
    if (!isEditing) setIsEditing(true);

    const newFields = editedSection.fields.map((field: DialogField) => {
      if (field.id === fieldId && field.options) {
        const newOptions = field.options.filter((_, idx) => idx !== optionIndex);
        // Reorder
        return {
          ...field,
          options: newOptions.map((opt, idx) => ({ ...opt, order_index: idx }))
        };
      }
      return field;
    });

    const updatedSection = { ...editedSection, fields: newFields };
    setEditedSection(updatedSection);

    // Clear saved data tracking since we're making new changes
    setLastSavedData(null);

    // Real-time update - notify context immediately
    if (onSectionUpdate) {
      onSectionUpdate(currentSectionIndex, updatedSection);
    }
  };

  // Handle save
  const handleSave = async () => {
    if (!editedSection || !currentDialog || !selectedGame?.id) return;

    setIsSaving(true);
    try {
      // Save any pending translation edits to TMS first
      if (isTranslationMode && displayLanguage && effectiveGameId && Object.keys(translationEdits).length > 0) {
        const translationPromises: Promise<void>[] = [];

        for (const [key, text] of Object.entries(translationEdits)) {
          if (key.includes(':')) {
            // Option translation – key is "fieldId:optionId"
            const [fieldId, optionId] = key.split(':');
            const field = editedSection.fields?.find((f: DialogField) => f.id === fieldId);
            const option = field?.options?.find((o: DialogOption) => o.id === optionId);
            if (option?.string_id) {
              translationPromises.push(
                saveFieldTranslation(option.string_id, displayLanguage, text, effectiveGameId)
              );
            }
          } else {
            // Field translation – key is fieldId
            const field = editedSection.fields?.find((f: DialogField) => f.id === key);
            if (field?.string_id) {
              translationPromises.push(
                saveFieldTranslation(field.string_id, displayLanguage, text, effectiveGameId)
              );
            }
          }
        }

        await Promise.all(translationPromises);
        setTranslationEdits({});
      }

      // Update the dialog's sections array
      const newSections = currentDialog.data?.sections?.map((section, index) =>
        index === currentSectionIndex ? editedSection : section
      ) || [];

      // Use metadata from context if available (preserves unsaved changes from wizard)
      // This prevents overwriting speaker mode, translatable, etc. that were changed in basic info
      const dialogData = dialogSectionContext?.dialogMetadata ? {
        ...currentDialog.data,
        sections: newSections,
        speakerMode: dialogSectionContext.dialogMetadata.speakerMode,
        speakerTypeId: dialogSectionContext.dialogMetadata.speakerTypeId,
        speakerTemplateIds: dialogSectionContext.dialogMetadata.speakerTemplateIds,
        translatable: dialogSectionContext.dialogMetadata.translatable,
        startingSection: dialogSectionContext.dialogMetadata.startingSection,
        variables: dialogSectionContext.dialogMetadata.dialogVariables,
      } : {
        ...currentDialog.data,
        sections: newSections
      };

      // Update dialog using apiService
      // Use metadata from context if available to preserve all unsaved wizard changes
      const result = await apiService.updateDialog({
        id: currentDialog.id,
        game_id: selectedGame.id,
        name: dialogSectionContext?.dialogMetadata?.dialogName || currentDialog.name,
        dialog_key: dialogSectionContext?.dialogMetadata?.dialogKey || currentDialog.dialog_key,
        description: dialogSectionContext?.dialogMetadata?.description || currentDialog.description,
        tags: dialogSectionContext?.dialogMetadata?.tags || currentDialog.tags,
        folder: currentDialog.folder,
        data: dialogData,
        status: dialogSectionContext?.dialogMetadata?.status || currentDialog.status,
      });

      if (!result.success) {
        throw new Error(result.error || 'Failed to update dialog section');
      }

      // Update local store
      const currentData = dialogStore.getData();
      const updatedData = currentData.map(item =>
        item.id === currentDialog.id ? result.data : item
      );
      dialogStore.setData(updatedData);

      // Notify parent component if callback provided
      if (onSectionUpdate) {
        onSectionUpdate(currentSectionIndex, editedSection);
      }

      toast({
        title: t('platform.common.success'),
        description: 'Section details updated successfully',
      });

      // Track the saved data to prevent stale context from overwriting it
      setLastSavedData(JSON.stringify(editedSection));

      // Reset editing state
      setIsEditing(false);

      // Trigger a page refresh to sync with flow and edit views
      window.dispatchEvent(new CustomEvent('dialog-section-updated', {
        detail: { dialogId: currentDialog.id, sectionIndex: currentSectionIndex }
      }));
    } catch (error) {
      console.error('Failed to save section details:', error);
      toast({
        title: t('platform.common.error'),
        description: error instanceof Error ? error.message : 'Failed to save section details',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    setEditedSection(currentSection ? JSON.parse(JSON.stringify(currentSection)) : null);
    setIsEditing(false);
    setLastSavedData(null);
    setTranslationEdits({});
  };

  // Show empty state if no dialog or section selected
  if (!currentDialog || !currentSection || !editedSection) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        <p>{t('platform.layout.structure-menu.select_a_section_to_view_details')}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-background">
      <div className="flex-1 overflow-y-auto p-4">
        <div className="space-y-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              Section Details
            </h3>
          </div>

          {/* Section Name */}
          <div>
            <Label className="text-sm font-medium ">Section Name</Label>
            <Input
              value={editedSection.name || ''}
              onChange={(e) => {
                if (!isEditing) setIsEditing(true);
                setEditedSection({ ...editedSection, name: e.target.value });
                setLastSavedData(null); // Clear saved data tracking since we're making new changes
                recordActivity(); // Track activity for smart auto-save
              }}
              className="mt-1 border border-border rounded p-2 w-full"
              placeholder="Section name"
            />
          </div>

          {/* Section Identifier (Read-only) */}
          <div>
            <Label className="text-sm font-medium text-muted-foreground">Section Identifier</Label>
            <Input
              value={editedSection.section_identifier || ''}
              className="mt-1 bg-muted border border-border rounded p-2 w-full"
              disabled
            />
            <p className="text-xs text-muted-foreground mt-1">
              Auto-generated from section name
            </p>
          </div>

          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide py-4">
            Section Data
          </h3>
          {/* Fields */}
          <div className=" ">
            {editedSection.fields && editedSection.fields.length > 0 ? (
              <div className="space-y-3">
                {editedSection.fields.map((field: DialogField, fieldIndex: number) => (
                  <div key={field.id} className="  ">
                    <div className="flex items-center justify-between mb-3">
                      <Select
                        value={field.type}
                        onValueChange={(value) => updateField(field.id, { type: value as any })}
                      >
                        <SelectTrigger className="text-xs border border-border rounded p-2 w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="dialog">Dialog</SelectItem>
                          <SelectItem value="context">Context</SelectItem>
                          <SelectItem value="question">Question</SelectItem>
                          <SelectItem value="condition">Condition</SelectItem>
                          <SelectItem value="setter">Setter</SelectItem>
                          <SelectItem value="end-dialog">End-Dialog</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-3">
                      {/* Field Text - not shown for setter or condition fields */}
                      {field.type !== 'setter' && field.type !== 'condition' && (
                        <div>
                          <Label className="text-xs">
                            Text
                            {isTranslationMode && field.string_id && field.translatable && (
                              <span className="ml-1 text-blue-500 font-normal">(translation)</span>
                            )}
                          </Label>
                          <TextAreaElement
                            value={getFieldTextValue(field)}
                            onChange={(value) => handleFieldTextChange(field, value)}
                            placeholder="Field text"
                            rows={2}
                            className="mt-1 text-sm"
                          />
                        </div>
                      )}

                      {/* Translatable - not shown for setter or condition fields */}
                      {field.type !== 'setter' && field.type !== 'condition' && (
                        <div className="flex items-center gap-2">
                          <Checkbox
                            id={`translatable-${field.id}`}
                            checked={field.translatable}
                            onCheckedChange={(checked) =>
                              updateField(field.id, { translatable: !!checked })
                            }
                          />
                          <Label htmlFor={`translatable-${field.id}`} className="text-xs">
                            Translatable
                          </Label>
                        </div>
                      )}

                      {/* Speaker (for dialog and question fields when speaker attribution is enabled) */}
                      {speakerMode !== 'none' && (field.type === 'dialog' || field.type === 'question') && (
                        <div className="w-full">
                          <Label className="text-xs w-">Speaker</Label>
                          {speakerMode === 'free-text' && (
                            <Input
                              value={field.speaker || ''}
                              onChange={(e) => updateField(field.id, { speaker: e.target.value })}
                              className="mt-1 text-sm border border-border rounded p-2 w-full"
                              placeholder="Speaker name"
                            />
                          )}
                          {speakerMode === 'type' && (
                            <Select
                              value={field.speaker || ''}
                              onValueChange={(value) => updateField(field.id, { speaker: value })}
                            >
                              <SelectTrigger className="mt-1 text-sm border border-border rounded p-2 w-full">
                                <SelectValue placeholder="Select speaker" />
                              </SelectTrigger>
                              <SelectContent>
                                {typeElements.map((element: TypeElement) => (
                                  <SelectItem key={element.id} value={element.id}>
                                    {element.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                          {speakerMode === 'object' && (
                            <Select
                              value={field.speaker || ''}
                              onValueChange={(value) => updateField(field.id, { speaker: value })}
                            >
                              <SelectTrigger className="mt-1 text-sm border border-border rounded p-2 w-full">
                                <SelectValue placeholder="Select speaker" />
                              </SelectTrigger>
                              <SelectContent>
                                {availableObjects.map((obj) => (
                                  <SelectItem key={obj.id} value={obj.id}>
                                    {obj.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                      )}

                      {/* Condition Configuration (for condition fields) */}
                      {field.type === 'condition' && (
                        <div className="border-t pt-3 space-y-3">
                          <Label className="text-xs font-medium">Condition Configuration</Label>
                          
                          {/* Variable Selector */}
                          <div>
                            <Label className="text-xs">Variable to Check</Label>
                            <Select
                              value={field.condition?.variable || ''}
                              onValueChange={(value) => {
                                const selectedVar = dialogVariables.find((v: DialogVariable) => v.name === value);
                                const variableType = selectedVar?.type;
                                
                                // Base update
                                const updates: Partial<DialogField> = {
                                  condition: {
                                    ...field.condition,
                                    variable: value
                                  }
                                };
                                
                                // Auto-create condition options based on variable type
                                if (variableType === 'boolean') {
                                  // Auto-create True/False options for boolean variables
                                  updates.conditionOptions = [
                                    { 
                                      id: `bool-true-${Date.now()}`, 
                                      condition_value: true, 
                                      operator: '==', 
                                      order_index: 0,
                                      next_section: null,
                                      next_dialog: null,
                                      next_dialog_section: null
                                    },
                                    { 
                                      id: `bool-false-${Date.now() + 1}`, 
                                      condition_value: false, 
                                      operator: '==', 
                                      order_index: 1,
                                      next_section: null,
                                      next_dialog: null,
                                      next_dialog_section: null
                                    },
                                  ];
                                } else if (variableType === 'range-selector' && selectedVar?.typeStoreRef?.typeId) {
                                  // Auto-create range options from TypeItem
                                  import('@/lib/utils/typeStoreResolver').then(({ getTypeItemById }) => {
                                    const typeItem = getTypeItemById(selectedVar.typeStoreRef!.typeId);
                                    if (typeItem?.data) {
                                      const rangeElements = typeItem.data.filter(el => el.valueType === 'range');
                                      const rangeOptions = rangeElements.map((el, idx) => ({
                                        id: `range-${el.id}-${Date.now()}`,
                                        condition_value: el.id,
                                        operator: 'in_range' as const,
                                        order_index: idx,
                                        next_section: null,
                                        next_dialog: null,
                                        next_dialog_section: null
                                      }));
                                      
                                      // Update with range options asynchronously
                                      updateField(field.id, {
                                        ...updates,
                                        conditionOptions: rangeOptions
                                      });
                                    }
                                  });
                                  // For range-selector, update the field first without options
                                  // The async import will update with options
                                  updateField(field.id, updates);
                                  return;
                                }
                                
                                updateField(field.id, updates);
                              }}
                            >
                              <SelectTrigger className="mt-1 text-sm border border-border rounded p-2 w-full">
                                <SelectValue placeholder="Select variable" />
                              </SelectTrigger>
                              <SelectContent>
                                {dialogVariables && dialogVariables.length > 0 ? (
                                  dialogVariables.filter((v: DialogVariable) => v.name.trim()).map((v: DialogVariable) => (
                                    <SelectItem key={v.name} value={v.name}>
                                      {v.name} ({v.type})
                                    </SelectItem>
                                  ))
                                ) : (
                                  <SelectItem value="__no_vars__" disabled>
                                    No variables declared
                                  </SelectItem>
                                )}
                              </SelectContent>
                            </Select>
                          </div>

                          {/* CheckVariable (for range-selector type) */}
                          {(() => {
                            const selectedVar = dialogVariables.find((v: DialogVariable) => v.name === field.condition?.variable);
                            return selectedVar?.type === 'range-selector' ? (
                              <div>
                                <Label className="text-xs">Value to Check (Variable)</Label>
                                <Select
                                  value={field.condition?.checkVariable || ''}
                                  onValueChange={(value) => {
                                    updateField(field.id, {
                                      condition: {
                                        ...field.condition,
                                        variable: field.condition?.variable || '',
                                        checkVariable: value
                                      }
                                    });
                                  }}
                                >
                                  <SelectTrigger className="mt-1 text-sm border border-border rounded p-2 w-full">
                                    <SelectValue placeholder="Select variable to check" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {dialogVariables.filter((v: DialogVariable) => v.type === 'number' || v.type === 'range').map((v: DialogVariable) => (
                                      <SelectItem key={v.name} value={v.name}>
                                        {v.name}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <p className="text-xs text-muted-foreground mt-1">
                                  Which numeric variable value should be checked against the ranges
                                </p>
                              </div>
                            ) : null;
                          })()}

                          {/* Condition Options */}
                          <div className="border-t pt-3">
                            <div className="flex items-center justify-between mb-2">
                              <Label className="text-xs font-medium">Condition Branches</Label>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => addConditionOption(field.id)}
                                className="h-6 text-xs"
                                disabled={!field.condition?.variable}
                              >
                                <Plus className="h-3 w-3 mr-1" />
                                Add Branch
                              </Button>
                            </div>

                            {field.conditionOptions && field.conditionOptions.length > 0 ? (
                              <div className="space-y-2">
                                {field.conditionOptions.map((condOption: any, condIndex: number) => (
                                  <div key={condOption.id || condIndex} className="border rounded p-2 bg-muted/30">
                                    <div className="flex items-center justify-between mb-2">
                                      <Label className="text-xs">Branch {condIndex + 1}</Label>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => removeConditionOption(field.id, condIndex)}
                                        className="h-5 w-5 p-0 text-destructive"
                                      >
                                        <Trash2 className="h-3 w-3" />
                                      </Button>
                                    </div>

                                    {/* Operator */}
                                    <div className="mb-2">
                                      <Label className="text-xs">Operator</Label>
                                      <Select
                                        value={condOption.operator || '=='}
                                        onValueChange={(value) => updateConditionOption(field.id, condIndex, { operator: value })}
                                      >
                                        <SelectTrigger className="mt-1 h-7 text-xs">
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="==">Equals (==)</SelectItem>
                                          <SelectItem value="!=">Not Equals (!=)</SelectItem>
                                          <SelectItem value=">">Greater Than (&gt;)</SelectItem>
                                          <SelectItem value="<">Less Than (&lt;)</SelectItem>
                                          <SelectItem value=">=">Greater or Equal (&gt;=)</SelectItem>
                                          <SelectItem value="<=">Less or Equal (&lt;=)</SelectItem>
                                          <SelectItem value="contains">Contains</SelectItem>
                                          <SelectItem value="startsWith">Starts With</SelectItem>
                                          <SelectItem value="endsWith">Ends With</SelectItem>
                                          <SelectItem value="in_range">In Range</SelectItem>
                                        </SelectContent>
                                      </Select>
                                    </div>

                                    {/* Condition Value */}
                                    <div className="mb-2">
                                      <Label className="text-xs">Value</Label>
                                      {(() => {
                                        const selectedVar = dialogVariables.find((v: DialogVariable) => v.name === field.condition?.variable);
                                        if (!selectedVar) {
                                          return (
                                            <Input
                                              value={String(condOption.condition_value ?? '')}
                                              onChange={(e) => updateConditionOption(field.id, condIndex, { condition_value: e.target.value })}
                                              className="mt-1 h-7 text-xs"
                                              placeholder="Enter value"
                                            />
                                          );
                                        }

                                        if (selectedVar.type === 'boolean') {
                                          return (
                                            <Select
                                              value={String(condOption.condition_value ?? 'false')}
                                              onValueChange={(value) => updateConditionOption(field.id, condIndex, { condition_value: value === 'true' })}
                                            >
                                              <SelectTrigger className="mt-1 h-7 text-xs">
                                                <SelectValue />
                                              </SelectTrigger>
                                              <SelectContent>
                                                <SelectItem value="true">true</SelectItem>
                                                <SelectItem value="false">false</SelectItem>
                                              </SelectContent>
                                            </Select>
                                          );
                                        } else if (selectedVar.type === 'range-selector' && selectedVar.typeStoreRef?.typeId) {
                                          // Range selector - show dropdown with available ranges
                                          const typeItem = getTypeItemById(selectedVar.typeStoreRef.typeId);
                                          const ranges = typeItem?.data?.filter(el => el.valueType === 'range') || [];
                                          
                                          return (
                                            <Select
                                              value={String(condOption.condition_value ?? '')}
                                              onValueChange={(value) => updateConditionOption(field.id, condIndex, { condition_value: value })}
                                            >
                                              <SelectTrigger className="mt-1 h-7 text-xs">
                                                <SelectValue placeholder="Select range..." />
                                              </SelectTrigger>
                                              <SelectContent>
                                                {ranges.length > 0 ? (
                                                  ranges.map((range) => (
                                                    <SelectItem key={range.id} value={range.id}>
                                                      {range.name} ({range.rangeMin ?? 0} - {range.rangeMax ?? 100})
                                                    </SelectItem>
                                                  ))
                                                ) : (
                                                  <SelectItem value="__no_ranges__" disabled>
                                                    No ranges available
                                                  </SelectItem>
                                                )}
                                              </SelectContent>
                                            </Select>
                                          );
                                        } else if (selectedVar.type === 'number' || selectedVar.type === 'range') {
                                          return (
                                            <Input
                                              type="number"
                                              value={String(condOption.condition_value ?? 0)}
                                              onChange={(e) => updateConditionOption(field.id, condIndex, { condition_value: parseFloat(e.target.value) || 0 })}
                                              className="mt-1 h-7 text-xs"
                                              placeholder="Enter number"
                                            />
                                          );
                                        } else {
                                          return (
                                            <Input
                                              type="text"
                                              value={String(condOption.condition_value ?? '')}
                                              onChange={(e) => updateConditionOption(field.id, condIndex, { condition_value: e.target.value })}
                                              className="mt-1 h-7 text-xs"
                                              placeholder="Enter text value"
                                            />
                                          );
                                        }
                                      })()}
                                    </div>

                                    {/* Navigation */}
                                    <div className="mb-2">
                                      <Label className="text-xs">Navigate To</Label>
                                      <Select
                                        value={condOption.next_section || condOption.next_dialog || '__none__'}
                                        onValueChange={(value) => {
                                          if (value === '__none__') {
                                            updateConditionOption(field.id, condIndex, { next_section: null, next_dialog: null });
                                          } else if (value.startsWith('dialog_')) {
                                            updateConditionOption(field.id, condIndex, { next_section: null, next_dialog: value.replace('dialog_', '') });
                                          } else {
                                            updateConditionOption(field.id, condIndex, { next_section: value, next_dialog: null });
                                          }
                                        }}
                                      >
                                        <SelectTrigger className="mt-1 h-7 text-xs">
                                          <SelectValue placeholder="Select target" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="__none__">None (continue)</SelectItem>
                                          {allSectionIdentifiers.map(s => (
                                            <SelectItem key={s.identifier} value={s.identifier}>
                                              Section: {s.name}
                                            </SelectItem>
                                          ))}
                                          {allDialogs.map(d => (
                                            <SelectItem key={d.id} value={`dialog_${d.id}`}>
                                              Dialog: {d.name}
                                            </SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-xs text-muted-foreground text-center py-2">
                                No branches yet. Click "Add Branch" to create one.
                              </p>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Setter Configuration (for setter fields) */}
                      {field.type === 'setter' && (
                        <div className="border-t pt-3 space-y-3">
                          <Label className="text-xs font-medium">Setter Configuration</Label>
                          
                          {/* Variable Selector */}
                          <div>
                            <Label className="text-xs">Variable</Label>
                            <Select
                              value={field.setter?.variable || ''}
                              onValueChange={(value) => {
                                const selectedVar = dialogVariables.find(v => v.name === value);
                                updateField(field.id, {
                                  setter: {
                                    ...field.setter,
                                    variable: value,
                                    operator: field.setter?.operator || 'set',
                                    value: field.setter?.value ?? (selectedVar?.type === 'boolean' ? false : selectedVar?.type === 'text' ? '' : 0)
                                  }
                                });
                              }}
                            >
                              <SelectTrigger className="mt-1 text-sm border border-border rounded p-2 w-full">
                                <SelectValue placeholder="Select variable" />
                              </SelectTrigger>
                              <SelectContent>
                                {dialogVariables && dialogVariables.length > 0 ? (
                                  dialogVariables.filter((v: DialogVariable) => v.name.trim()).map((v: DialogVariable) => (
                                    <SelectItem key={v.name} value={v.name}>
                                      {v.name} ({v.type})
                                    </SelectItem>
                                  ))
                                ) : (
                                  <SelectItem value="__no_vars__" disabled>
                                    No variables declared
                                  </SelectItem>
                                )}
                              </SelectContent>
                            </Select>
                          </div>

                          {/* Operation Selector */}
                          <div>
                            <Label className="text-xs">Operation</Label>
                            <Select
                              value={field.setter?.operator || 'set'}
                              onValueChange={(value: 'set' | 'increment' | 'decrement') => {
                                updateField(field.id, {
                                  setter: {
                                    ...field.setter,
                                    variable: field.setter?.variable || '',
                                    operator: value,
                                    value: field.setter?.value ?? 0
                                  }
                                });
                              }}
                            >
                              <SelectTrigger className="mt-1 text-sm border border-border rounded p-2 w-full">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="set">Set</SelectItem>
                                <SelectItem value="increment">Increment</SelectItem>
                                <SelectItem value="decrement">Decrement</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          {/* Value Input */}
                          <div>
                            <Label className="text-xs">Value</Label>
                            {(() => {
                              const selectedVar = dialogVariables.find((v: DialogVariable) => v.name === field.setter?.variable);
                              if (!selectedVar) {
                                return (
                                  <Input
                                    value={String(field.setter?.value ?? '')}
                                    onChange={(e) => {
                                      updateField(field.id, {
                                        setter: {
                                          ...field.setter,
                                          variable: field.setter?.variable || '',
                                          operator: field.setter?.operator || 'set',
                                          value: e.target.value
                                        }
                                      });
                                    }}
                                    className="mt-1 text-sm border border-border rounded p-2 w-full"
                                    placeholder="Select a variable first"
                                  />
                                );
                              }

                              if (selectedVar.type === 'boolean') {
                                return (
                                  <Select
                                    value={String(field.setter?.value ?? 'false')}
                                    onValueChange={(value) => {
                                      updateField(field.id, {
                                        setter: {
                                          ...field.setter,
                                          variable: field.setter?.variable || '',
                                          operator: field.setter?.operator || 'set',
                                          value: value === 'true'
                                        }
                                      });
                                    }}
                                  >
                                    <SelectTrigger className="mt-1 text-sm border border-border rounded p-2 w-full">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="true">true</SelectItem>
                                      <SelectItem value="false">false</SelectItem>
                                    </SelectContent>
                                  </Select>
                                );
                              } else if (selectedVar.type === 'number' || selectedVar.type === 'range') {
                                return (
                                  <Input
                                    type="number"
                                    value={String(field.setter?.value ?? 0)}
                                    onChange={(e) => {
                                      updateField(field.id, {
                                        setter: {
                                          ...field.setter,
                                          variable: field.setter?.variable || '',
                                          operator: field.setter?.operator || 'set',
                                          value: parseFloat(e.target.value) || 0
                                        }
                                      });
                                    }}
                                    className="mt-1 text-sm border border-border rounded p-2 w-full"
                                    placeholder="Enter number"
                                  />
                                );
                              } else {
                                return (
                                  <Input
                                    type="text"
                                    value={String(field.setter?.value ?? '')}
                                    onChange={(e) => {
                                      updateField(field.id, {
                                        setter: {
                                          ...field.setter,
                                          variable: field.setter?.variable || '',
                                          operator: field.setter?.operator || 'set',
                                          value: e.target.value
                                        }
                                      });
                                    }}
                                    className="mt-1 text-sm border border-border rounded p-2 w-full"
                                    placeholder="Enter text value"
                                  />
                                );
                              }
                            })()}
                          </div>
                        </div>
                      )}

                      {/* Options (for question fields) */}
                      {field.type === 'question' && (
                        <div className="border-t pt-3">
                          <div className="flex items-center justify-between mb-2">
                            <Label className="text-xs font-medium">Options</Label>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => addOption(field.id)}
                              className="h-6 text-xs"
                            >
                              <Plus className="h-3 w-3 mr-1" />
                              Add Option
                            </Button>
                          </div>

                          {field.options && field.options.length > 0 ? (
                            <div className="space-y-2">
                              {field.options.map((option, optIndex) => (
                                <div key={option.id || optIndex} className="border rounded p-2 bg-muted/30">
                                  <div className="flex items-center justify-between mb-2">
                                    <Label className="text-xs">Option {optIndex + 1}</Label>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => removeOption(field.id, optIndex)}
                                      className="h-5 w-5 p-0 text-destructive"
                                    >
                                      <Trash2 className="h-3 w-3" />
                                    </Button>
                                  </div>

                                  {/* Option Text */}
                                  <div className="mb-2">
                                    <Label className="text-xs">
                                      Text
                                      {isTranslationMode && option.string_id && (
                                        <span className="ml-1 text-blue-500 font-normal">(translation)</span>
                                      )}
                                    </Label>
                                    <Input
                                      value={getOptionTextValue(field, option)}
                                      onChange={(e) =>
                                        handleOptionTextChange(field, option, optIndex, e.target.value)
                                      }
                                      className="mt-1 h-7 text-xs"
                                      placeholder="Option text"
                                    />
                                  </div>

                                  {/* Translatable */}
                                  <div className="flex items-center gap-2 mb-2">
                                    <Checkbox
                                      id={`opt-translatable-${option.id}`}
                                      checked={option.translatable}
                                      onCheckedChange={(checked) =>
                                        updateOption(field.id, optIndex, { translatable: !!checked })
                                      }
                                    />
                                    <Label htmlFor={`opt-translatable-${option.id}`} className="text-xs">
                                      Translatable
                                    </Label>
                                  </div>




                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-xs text-muted-foreground text-center py-2">
                              No options yet. Click "Add Option" to create one.
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground text-center py-4">
                No fields in this section
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Save/Cancel buttons */}
      {isEditing && (
        <div className="p-3 border-t border-border bg-muted flex gap-2">
          <Button
            onClick={handleSave}
            disabled={isSaving}
            className="flex-1"
            variant="default"
          >
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save
              </>
            )}
          </Button>
          <Button
            onClick={handleCancel}
            disabled={isSaving}
            variant="outline"
          >
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
}
